<template>
    <v-container fluid>
        <v-data-table
            :headers="headers"
            :items="machinesFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">MACHINES</h1>
                        <v-btn
                            rounded
                            color="primary"
                            @click="openCreateMachine"
                            class="mx-2"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.name`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByName"
                    dense
                    prepend-icon="mdi-filter"
                />
            </template>
            <template v-slot:[`header.process`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByProcess"
                    dense
                    prepend-icon="mdi-filter"
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.process`]="{ item }">
                <p class="mb-0">
                    {{ processes.find(i => i.id == item.process)?.name }}
                </p>
            </template>
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openEditMachine(item)">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn small icon @click="openDeleteMachine(item)">
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--PROCESSES FORM-->
        <v-dialog
            v-model="openMachineForm"
            :retain-focus="false"
            persistent
            width="600px"
        >
            <MachinesForm
                v-if="openMachineForm"
                :createForm="createForm"
                :machine="selectedMachine"
                :originalMachine="originalMachine"
                :settingId="setting.id"
                :processes="processes"
                @addMachine="addMachine"
                @replaceMachine="replaceMachine"
                @close="closeEditMachine"
            />
        </v-dialog>
        <!--Delete Machine-->
        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5">Delete Machine</v-card-title>
                <v-card-text>
                    Are you sure you want to delete this machine?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteMachine">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteMachine"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'

export default {
    name: 'Machines',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        MachinesForm: () => import('@/components/Machines/MachinesForm.vue'),
    },
    data: () => ({
        loading: false,
        processes: [],
        machines: [],
        selectedMachine: {},
        originalMachine: {},
        createForm: true,
        openMachineForm: false,
        openDeleteWarning: false,
        settings: {},
        headers: [
            {
                text: 'NAME',
                value: 'name',
                sortable: false,
            },
            {
                text: 'PROCESS',
                value: 'process',
                sortable: false,
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                sortable: false,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                sortable: false,
                align: 'center',
            },
        ],
        searchByName: undefined,
        searchByProcess: undefined,
    }),
    async mounted() {
        try {
            this.loading = true
            this.processes = await API.getProcesses(this.setting.id)
            this.machines = await API.getMachines(this.setting.id)
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        machinesFiltered() {
            const conditions = []
            if (this.searchByName) {
                conditions.push(this.filterByName)
            }
            if (this.searchByProcess) {
                conditions.push(this.filterByProcess)
            }
            if (conditions.length > 0) {
                return this.machines.filter(machine => {
                    return conditions.every(condition => {
                        return condition(machine)
                    })
                })
            }
            return this.machines
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        filterByName(item) {
            return item.name
                .toLowerCase()
                .includes(this.searchByName.toLowerCase())
        },
        filterByProcess(item) {
            return this.processes
                .find(i => i.id == item.process)
                .name.toLowerCase()
                .includes(this.searchByProcess.toLowerCase())
        },
        replaceMachine(machineToUpdate) {
            const index = this.machines.findIndex(
                machine => machine.id == machineToUpdate.id
            )
            if (index >= 0) {
                this.machines.splice(index, 1, {
                    ...this.machines[index],
                    ...machineToUpdate,
                })
            }
        },
        addMachine(machine) {
            if (this.machines && this.machines.length > 0) {
                this.machines = [...this.machines, machine]
            } else {
                this.machines = [machine]
            }
        },
        removeMachine() {
            const index = this.machines.findIndex(
                machine => machine.id == this.selectedMachine.id
            )
            if (index >= 0) {
                this.machines.splice(index, 1)
            }
        },
        async deleteMachine() {
            try {
                this.loading = true
                await API.deleteMachine({
                    settingId: this.setting.id,
                    machineId: this.selectedMachine.id,
                })
                this.removeMachine()
                this.closeDeleteMachine()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteMachine() {
            this.selectedMachine = {}
            this.openDeleteWarning = false
        },
        openDeleteMachine(item) {
            this.selectedMachine = _.cloneDeep(item)
            this.openDeleteWarning = true
        },
        closeEditMachine() {
            this.createForm = true
            this.selectedMachine = {}
            this.originalMachine = {}
            this.openMachineForm = false
        },
        openEditMachine(item) {
            this.createForm = false
            this.selectedMachine = _.cloneDeep(item)
            this.originalMachine = _.cloneDeep(item)
            this.openMachineForm = true
        },
        openCreateMachine() {
            this.createForm = true
            this.openMachineForm = true
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
}
.v-input .v-label {
    font-size: 12px;
}
</style>
